/**
 * 异步导出功能 Mixin
 * 提供大文件异步导出功能，支持状态轮询和进度显示
 * 使用方法：
 * 1. 在组件中引入：import { AsyncExportMixin } from '@/mixins/asyncExportMixin'
 * 2. 在mixins中添加：mixins: [AsyncExportMixin]
 * 3. 在data中配置导出相关URL：
 *    url: {
 *      exportTaskUrl: "/ndt/export/task/xxx/async",
 *      exportStatusUrl: "/ndt/export/task/status",
 *      exportDownloadUrl: "ndt/export/task/download"
 *    }
 * 4. 在模板中使用：
 *    <a-button @click="handleAsyncExportXls()" :loading="exportLoading">
 *      {{ exportLoading ? '导出中...' : '导出' }}
 *    </a-button>
 */

import { postRequest, getRequest } from '@/libs/axios'
import { generateRandomFileName } from '@/utils/util'
import axios from '@/libs/axios'

export const AsyncExportMixin = {
  data() {
    return {
      exportLoading: false, // 导出加载状态
      exportTaskId: null, // 导出任务ID
      exportTimer: null, // 轮询定时器
    }
  },

  beforeDestroy() {
    // 组件销毁时清理定时器
    if (this.exportTimer) {
      clearTimeout(this.exportTimer)
      this.exportTimer = null
    }
  },

  methods: {
    /**
     * 异步导出主方法
     * @param {string} fileName - 文件名（可选）
     * @param {object} customParams - 自定义参数（可选）
     */
    async handleAsyncExportXls(fileName, customParams = {}) {
      if (!fileName || typeof fileName != "string") {
        fileName = generateRandomFileName()
      }

      // 检查必要的URL配置
      if (!this.url?.exportTaskUrl || !this.url?.exportStatusUrl) {
        this.$message.error('导出配置不完整，请检查exportTaskUrl和exportStatusUrl配置')
        return
      }

      let param = this.getQueryParams ? this.getQueryParams() : {}

      // 合并自定义参数
      param = { ...param, ...customParams }

      // 添加选中项参数
      if (this.selectedRowKeys && this.selectedRowKeys.length > 0) {
        param['selections'] = this.selectedRowKeys.join(",")
      }

      try {
        this.exportLoading = true
        this.$message.loading('正在准备导出文件，请稍候...', 0)
        // 1. 发起导出任务
        const taskResponse = await postRequest(this.url.exportTaskUrl, param)

        if (taskResponse.code != 200) {
          this.$message.destroy()
          this.$message.error(taskResponse.data?.message || '导出任务创建失败')
          this.exportLoading = false
          return
        }

        this.exportTaskId = taskResponse.data

        this.$message.destroy()
        this.$message.loading('文件生成中，请稍候...', 0)

        // 2. 轮询检查导出状态
        this.checkAsyncExportStatus(fileName)

      } catch (error) {
        console.error('导出失败:', error)
        this.$message.destroy()
        this.$message.error('导出失败: ' + (error.message || '未知错误'))
        this.exportLoading = false
      }
    },

    /**
     * 检查异步导出状态
     * @param {string} fileName - 文件名
     */
    async checkAsyncExportStatus(fileName) {
      if (!this.exportTaskId) return

      try {
        const response = await getRequest(`${this.url.exportStatusUrl}/${this.exportTaskId}`)
        const { code, data } = response
        if (code != 200) {
          this.$message.destroy()
          this.$message.error('检查导出状态失败')
          this.exportLoading = false
          return
        }
        const { status, progress, errorMessage, realPath } = data
        switch (status) {
          case 0: // 待处理
            this.$message.destroy()
            this.$message.loading(`任务待处理，请稍候...`, 0)
            this.exportTimer = setTimeout(() => {
              this.checkAsyncExportStatus(fileName)
            }, 2000)
            break

          case 1: // 处理中
            this.$message.destroy()
            this.$message.loading(`文件生成中 ${progress || 0}%，请稍候...`, 0)
            this.exportTimer = setTimeout(() => {
              this.checkAsyncExportStatus(fileName)
            }, 2000)
            break

          case 2: // 已完成
            this.$message.destroy()
            this.$message.success('文件生成完成，开始下载...')

            // 优先使用realPath直接下载
            if (realPath) {
              this.downloadFileByPath(realPath, fileName + '.xlsx')
            } else {
              this.downloadAsyncFile(this.exportTaskId, fileName)
            }

            this.exportLoading = false
            this.exportTaskId = null
            break

          case 3: // 失败
            this.$message.destroy()
            this.$message.error('文件生成失败: ' + (errorMessage || '未知错误'))
            this.exportLoading = false
            this.exportTaskId = null
            break

          default:
            this.exportTimer = setTimeout(() => {
              this.checkAsyncExportStatus(fileName)
            }, 2000)
        }

      } catch (error) {
        console.error('检查导出状态失败:', error)
        this.$message.destroy()
        this.$message.error('检查导出状态失败')
        this.exportLoading = false
      }
    },

    /**
     * 通过路径下载文件（避免空白页）
     * @param {string} filePath - 文件路径
     * @param {string} fileName - 文件名
     */
    downloadFileByPath(filePath, fileName) {
      try {
        // 创建隐藏的下载链接，避免打开新页面
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = filePath
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()

        // 清理
        setTimeout(() => {
          document.body.removeChild(link)
        }, 100)
      } catch (error) {
        console.error('下载文件失败:', error)
        this.$message.error('下载文件失败')
      }
    },

    /**
     * 通过任务ID下载文件（备用方案）
     * @param {string} taskId - 任务ID
     * @param {string} fileName - 文件名
     */
    async downloadAsyncFile(taskId, fileName) {
      if (!this.url?.exportDownloadUrl) {
        this.$message.error('下载配置不完整，请检查exportDownloadUrl配置')
        return
      }

      try {
        // 构建下载URL
        const downloadUrl = `${this.url.exportDownloadUrl}/${taskId}`
        // 使用axios直接下载，确保携带认证信息
        const response = await axios({
          method: 'get',
          url: downloadUrl,
          responseType: 'blob'
        })

        // 创建下载链接
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = url
        link.setAttribute('download', fileName + '.xlsx')
        document.body.appendChild(link)
        link.click()

        // 清理
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }, 100)
      } catch (error) {
        console.error('下载文件失败:', error)
        this.$message.error('下载文件失败: ' + (error.message || '未知错误'))
      }
    },

    /**
     * 停止导出任务
     */
    stopAsyncExport() {
      if (this.exportTimer) {
        clearTimeout(this.exportTimer)
        this.exportTimer = null
      }

      this.exportLoading = false
      this.exportTaskId = null
      this.$message.destroy()
      this.$message.info('导出任务已取消')
    }
  }
}

export default AsyncExportMixin
